<template>
    <div v-if="isPanelAvailable" class="power-control-panel-wrapper">
        <!-- Floating Left Trigger Handle (Discreet, accessible for beta users) -->
        <div 
            class="power-trigger-tab shadow-sm" 
            :class="{ 'is-open': isOpen }" 
            @click="togglePanel"
            v-b-tooltip.hover.right="'Beta Info & Changelog'"
            data-test="btn-trigger-beta-info"
            aria-label="Beta system and changelog info"
        >
            <div class="d-flex align-items-center justify-content-center">
                <i class="bi bi-stars power-trigger-icon"></i>
            </div>
        </div>

        <!-- Backdrop Overlay -->
        <div v-if="isOpen" class="power-drawer-backdrop" @click="isOpen = false"></div>

        <!-- Slide-out Drawer Panel -->
        <transition name="slide-drawer">
            <div v-if="isOpen" class="power-drawer-panel shadow-lg" data-test="panel-beta-info">
                <!-- Header -->
                <div class="power-drawer-header d-flex justify-content-between align-items-center p-3 border-bottom drawer-border">
                    <div class="d-flex align-items-center">
                        <i class="bi bi-stars text-warning mr-2" style="font-size: 1.25rem;"></i>
                        <div>
                            <h6 class="font-weight-bold mb-0 drawer-heading-text">Beta System &amp; Changelog</h6>
                            <span class="extra-small text-muted">Build Info &amp; Resolved Issues</span>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <span class="badge badge-beta-amber font-weight-bold mr-2 px-2 py-1">
                            BETA
                        </span>
                        <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="isOpen = false" title="Close Panel (Esc)" data-test="btn-close-beta-info">
                            <i class="bi bi-x-lg" style="font-size: 1.1rem;"></i>
                        </button>
                    </div>
                </div>

                <!-- Navigation Tabs -->
                <div class="power-drawer-nav p-2 border-bottom drawer-border">
                    <div class="nav-segment-wrapper d-flex p-1 rounded">
                        <button 
                            type="button" 
                            class="nav-segment-btn flex-fill" 
                            :class="{ active: activeSection === 'changelog' }"
                            @click="selectSection('changelog')"
                            data-test="tab-beta-changelog"
                        >
                            <i class="bi bi-journal-text mr-1"></i> Changelog &amp; Updates ({{ changelogList.length }})
                        </button>
                        <button 
                            type="button" 
                            class="nav-segment-btn flex-fill" 
                            :class="{ active: activeSection === 'system' }"
                            @click="selectSection('system')"
                            data-test="tab-beta-system"
                        >
                            <i class="bi bi-cpu-fill mr-1"></i> System Info
                        </button>
                    </div>
                </div>

                <!-- Panel Body -->
                <div class="power-drawer-body p-3">
                    <!-- SECTION 1: CHANGELOG & RECENT UPDATES -->
                    <div v-show="activeSection === 'changelog'">
                        <!-- Search Box -->
                        <div class="mb-3">
                            <b-input-group size="sm">
                                <b-input-group-prepend is-text class="drawer-search-prepend text-muted">
                                    <i class="bi bi-search"></i>
                                </b-input-group-prepend>
                                <b-form-input 
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search updates (e.g. 'sprint', 'sso', 'azure')..."
                                    class="drawer-search-input small"
                                    data-test="input-search-changelog"
                                >
                                </b-form-input>
                                <b-input-group-append v-if="searchQuery">
                                    <b-button variant="outline-secondary" size="sm" @click="searchQuery = ''" title="Clear Search" class="drawer-clear-btn">
                                        <i class="bi bi-x"></i>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </div>

                        <!-- Changelog Category Filter Tabs -->
                        <div class="d-flex align-items-center mb-3 flex-wrap changelog-category-pills" role="tablist" aria-label="Changelog categories">
                            <button 
                                type="button" 
                                class="cat-filter-btn cat-filter-all" 
                                :class="{ active: changelogCategoryFilter === 'all' }"
                                :aria-pressed="changelogCategoryFilter === 'all' ? 'true' : 'false'"
                                @click="setCategoryFilter('all')"
                                data-test="filter-changelog-all"
                                title="Show all release updates"
                            >
                                All <span class="cat-count-badge ml-1">{{ changelogList.length }}</span>
                            </button>
                            <button 
                                type="button" 
                                class="cat-filter-btn cat-filter-features" 
                                :class="{ active: changelogCategoryFilter === 'features' }"
                                :aria-pressed="changelogCategoryFilter === 'features' ? 'true' : 'false'"
                                @click="setCategoryFilter('features')"
                                data-test="filter-changelog-features"
                                title="Show new feature additions"
                            >
                                ✨ Features <span class="cat-count-badge ml-1">{{ totalFeaturesEntries }}</span>
                            </button>
                            <button 
                                type="button" 
                                class="cat-filter-btn cat-filter-fixes" 
                                :class="{ active: changelogCategoryFilter === 'fixes' }"
                                :aria-pressed="changelogCategoryFilter === 'fixes' ? 'true' : 'false'"
                                @click="setCategoryFilter('fixes')"
                                data-test="filter-changelog-fixes"
                                title="Show bug and defect fixes"
                            >
                                🐛 Fixes <span class="cat-count-badge ml-1">{{ totalFixesEntries }}</span>
                            </button>
                            <button 
                                type="button" 
                                class="cat-filter-btn cat-filter-beta highlight-beta" 
                                :class="{ active: changelogCategoryFilter === 'beta-reports' }"
                                :aria-pressed="changelogCategoryFilter === 'beta-reports' ? 'true' : 'false'"
                                @click="setCategoryFilter('beta-reports')"
                                data-test="filter-changelog-beta-reports"
                                title="Show fixes resolving community Beta feedback and bug reports"
                            >
                                ⭐ Beta Reports <span class="cat-count-badge badge-beta-count ml-1">{{ totalBetaReportEntries }}</span>
                            </button>
                        </div>

                        <!-- Pinned: Highlights Card (Adapts to Active Filter) -->
                        <div v-if="!searchQuery && pinnedBuild" class="pinned-build-card p-3 rounded mb-3 border">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <span class="badge px-2 py-1 extra-small font-weight-bold" :class="pinnedBuildBadgeClass">
                                    <i class="bi mr-1" :class="pinnedBuildBadgeIcon"></i>{{ pinnedBuildBadgeText }}
                                </span>
                                <span class="extra-small text-muted font-monospace">Git: {{ gitCommit }}</span>
                            </div>
                            <div class="font-weight-bold drawer-heading-text small mb-1">{{ pinnedBuild.title }}</div>
                            <div class="extra-small text-muted mb-2">
                                <i class="bi bi-calendar3 mr-1"></i>{{ formatDate(pinnedBuild.date) }} • {{ getEntryFilterCountLabel(pinnedBuild) }}
                            </div>
                            <div class="extra-small drawer-body-text">
                                <div v-if="loadingEntries[pinnedBuild.id]" class="py-1">
                                    <load-spinner size="sm" inline text="Loading highlights..." />
                                </div>
                                <ul v-else-if="loadedEntries[pinnedBuild.id]" class="pl-3 mb-0">
                                    <li v-for="(it, idx) in getTopItems(loadedEntries[pinnedBuild.id], 3)" :key="idx" class="mb-1">
                                        <span>{{ getItemText(it) }}</span>
                                        <span 
                                            v-if="isReportedForCurrentUser(it)" 
                                            class="badge badge-success ml-1 extra-small"
                                            title="Resolved for your reported issue!"
                                        >
                                            <i class="bi bi-check2-circle mr-1"></i> Resolved for your report!
                                        </span>
                                        <span 
                                            v-else-if="isItemReported(it)" 
                                            class="badge badge-beta-reported ml-1 extra-small"
                                            :title="getItemReporter(it) ? 'Reported by ' + getItemReporter(it) : 'Reported by Beta User'"
                                        >
                                            <i class="bi bi-person-heart mr-1"></i> Beta Report
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <!-- Release History List Header -->
                        <div class="d-flex justify-content-between align-items-center mb-2 px-1">
                            <span class="extra-small text-muted font-weight-bold text-uppercase">
                                <i class="bi bi-clock-history mr-1"></i>Release History
                            </span>
                            <span class="extra-small text-muted">
                                {{ filteredChangelogs.length }} of {{ changelogList.length }} entries
                            </span>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loadingList" class="py-3">
                            <load-spinner size="sm" text="Loading release history..." />
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="filteredChangelogs.length === 0" class="text-center py-4 text-muted small">
                            <span v-if="searchQuery">
                                <i class="bi bi-search mr-1"></i> No matching updates found for "{{ searchQuery }}".
                            </span>
                            <span v-else-if="changelogCategoryFilter === 'beta-reports'">
                                <i class="bi bi-star mr-1"></i> No community-reported bug fixes in release history yet.
                            </span>
                            <span v-else-if="changelogCategoryFilter === 'fixes'">
                                <i class="bi bi-bug mr-1"></i> No bug fixes found for this view.
                            </span>
                            <span v-else-if="changelogCategoryFilter === 'features'">
                                <i class="bi bi-stars mr-1"></i> No feature updates found for this view.
                            </span>
                            <span v-else>
                                <i class="bi bi-journal-text mr-1"></i> No updates available yet.
                            </span>
                        </div>

                        <!-- Changelog Cards -->
                        <div 
                            v-else
                            v-for="entry in filteredChangelogs" 
                            :key="entry.id" 
                            class="changelog-entry-card p-3 rounded mb-2 border"
                            :class="{ 'entry-expanded': isExpanded(entry.id) }"
                        >
                            <!-- Entry Header (Clickable to toggle) -->
                            <div class="d-flex justify-content-between align-items-start cursor-pointer" @click="toggleEntry(entry.id)">
                                <div class="pr-2">
                                    <div class="d-flex align-items-center mb-1 flex-wrap">
                                        <span class="badge badge-secondary extra-small font-monospace mr-2 mb-1">
                                            {{ entry.date }}
                                        </span>
                                        <span v-for="cat in (entry.categories || [])" :key="cat" class="badge mr-1 mb-1 extra-small" :class="getCategoryBadgeClass(cat)">
                                            {{ cat }}
                                        </span>
                                        <span v-if="entry.hasBetaFixes" class="badge badge-warning text-dark mr-1 mb-1 extra-small font-weight-bold" title="Fixes community Beta reports">
                                            <i class="bi bi-star-fill mr-1"></i>Beta Fixes
                                        </span>
                                        <span class="extra-small text-muted font-weight-500 mb-1 ml-1">
                                            {{ getEntryFilterCountLabel(entry) }}
                                        </span>
                                    </div>
                                    <div class="font-weight-bold drawer-heading-text small entry-title">
                                        {{ entry.title }}
                                    </div>
                                </div>
                                <button type="button" class="btn btn-sm btn-link text-muted p-0 ml-1 flex-shrink-0" :aria-label="isExpanded(entry.id) ? 'Collapse' : 'Expand'">
                                    <i class="bi" :class="isExpanded(entry.id) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                                </button>
                            </div>

                            <!-- Entry Content (Expanded, on-demand loaded) -->
                            <div v-if="isExpanded(entry.id)" class="entry-details mt-3 pt-2 border-top drawer-border">
                                <div v-if="loadingEntries[entry.id]" class="py-2">
                                    <load-spinner size="sm" text="Loading release details..." />
                                </div>
                                <div v-else-if="loadedEntries[entry.id]">
                                    <div v-for="(sec, sIdx) in getFilteredSections(loadedEntries[entry.id])" :key="sIdx" class="mb-3">
                                        <div class="d-flex align-items-center mb-1">
                                            <span class="badge extra-small mr-2" :class="getCategoryBadgeClass(sec.category)">
                                                {{ sec.category }}
                                            </span>
                                            <span v-if="sec.scope" class="extra-small text-muted font-monospace text-truncate" style="max-width: 220px;" :title="sec.scope">
                                                {{ sec.scope }}
                                            </span>
                                        </div>
                                        <ul class="pl-3 mb-0 extra-small drawer-body-text">
                                            <li v-for="(item, iIdx) in sec.items" :key="iIdx" class="mb-2 changelog-item-row" style="line-height: 1.45;">
                                                <span>{{ getItemText(item) }}</span>

                                                <!-- Personal Resolution Badge -->
                                                <span 
                                                    v-if="isReportedForCurrentUser(item)" 
                                                    class="badge badge-success ml-2 py-1 px-2 extra-small text-nowrap d-inline-flex align-items-center"
                                                    title="This fix addresses an issue you reported during Beta testing!"
                                                    data-test="badge-user-report-resolved"
                                                >
                                                    <i class="bi bi-check2-circle mr-1"></i> Resolved for your report!
                                                </span>

                                                <!-- Community Beta Report Badge -->
                                                <span 
                                                    v-else-if="isItemReported(item)" 
                                                    class="badge badge-beta-reported ml-2 py-1 px-2 extra-small text-nowrap d-inline-flex align-items-center"
                                                    :title="getItemReporter(item) ? 'Reported by ' + getItemReporter(item) : 'Reported by Beta User'"
                                                    data-test="badge-beta-reported"
                                                >
                                                    <i class="bi bi-person-heart mr-1"></i>
                                                    <span v-if="getItemReporter(item)">Reported by {{ getItemReporter(item) }}</span>
                                                    <span v-else>Reported by Beta User</span>
                                                    <span v-if="getItemReportId(item)" class="ml-1 opacity-75 font-monospace">({{ getItemReportId(item) }})</span>
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div v-else class="text-muted extra-small py-2 text-center">
                                    Could not load details for this release.
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- SECTION 2: SYSTEM & BUILD INFO -->
                    <div v-show="activeSection === 'system'">
                        <!-- System Metadata Card -->
                        <div class="power-card p-3 rounded mb-3 border">
                            <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom drawer-border">
                                <div class="d-flex align-items-center">
                                    <i class="bi bi-cpu-fill text-info mr-2" style="font-size: 1.1rem;"></i>
                                    <strong class="drawer-heading-text small">System &amp; Build Metadata</strong>
                                </div>
                                <span class="badge badge-beta-amber px-2 py-1 extra-small">
                                    BETA
                                </span>
                            </div>
                            <div class="power-info-grid extra-small">
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Environment:</span>
                                    <span class="font-weight-bold drawer-heading-text text-capitalize d-flex align-items-center">
                                        <span class="system-env-dot mr-1" :class="'dot-' + envNameNormalized"></span>
                                        {{ envNameDisplay }} ({{ envBadgeLabel }})
                                    </span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Build Version:</span>
                                    <span class="font-weight-bold text-info">v{{ appVersion }}</span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Release Channel:</span>
                                    <span class="badge beta-channel-badge font-weight-bold">{{ releaseChannel }}</span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Git Revision:</span>
                                    <span class="font-monospace drawer-body-text font-weight-bold">{{ gitCommit }}</span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Build Timestamp:</span>
                                    <span class="drawer-body-text">{{ formattedBuildTime }}</span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Account:</span>
                                    <span class="text-success text-truncate ml-2" style="max-width: 170px;" :title="userEmail">{{ userEmail }}</span>
                                </div>
                                <div class="d-flex justify-content-between py-1">
                                    <span class="text-muted">Access Status:</span>
                                    <span class="text-warning font-weight-bold">{{ userPlanLabel }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Feedback & Issue Reporting Shortcut -->
                        <div class="power-card p-3 rounded mb-3 border">
                            <div class="font-weight-bold drawer-heading-text small mb-1">
                                <i class="bi bi-chat-heart text-info mr-1"></i> Reported an Issue?
                            </div>
                            <p class="extra-small text-muted mb-3">
                                Feedback submitted by Beta users directly informs our next build changelog.
                            </p>
                            <b-button variant="outline-info" size="sm" block class="py-2" @click="openFeedbackModal" data-test="btn-beta-open-feedback">
                                <i class="bi bi-send-fill mr-1"></i> Submit Feedback or Report Issue
                            </b-button>
                        </div>

                        <!-- Keyboard Shortcuts Card -->
                        <div class="power-card p-3 rounded mb-3 border">
                            <label class="font-weight-bold mb-2 drawer-heading-text small">
                                <i class="bi bi-keyboard text-warning mr-1"></i> Shortcuts &amp; Navigation
                            </label>
                            <div class="extra-small text-muted">
                                <div class="d-flex justify-content-between align-items-center py-1 border-bottom drawer-border">
                                    <span>Toggle Drawer:</span>
                                    <div><kbd>Ctrl</kbd> + <kbd>`</kbd> or <kbd>Alt</kbd> + <kbd>D</kbd></div>
                                </div>
                                <div class="d-flex justify-content-between align-items-center py-1">
                                    <span>Close Drawer:</span>
                                    <div><kbd>Esc</kbd></div>
                                </div>
                            </div>
                        </div>

                        <!-- Legal, Terms & Compliance Card -->
                        <div class="power-card p-3 rounded mb-3 border" data-test="card-beta-legal">
                            <label class="font-weight-bold mb-1 drawer-heading-text small d-flex align-items-center">
                                <i class="bi bi-shield-check text-success mr-2"></i> Legal &amp; Compliance
                            </label>
                            <p class="extra-small text-muted mb-2">
                                Review Terms of Service, GDPR Privacy Policy, and Acceptable Use.
                            </p>
                            <div class="d-flex align-items-center justify-content-between pt-1">
                                <b-button variant="outline-primary" size="sm" class="py-1 px-2 extra-small flex-fill mr-1" @click="openLegal('terms')" data-test="btn-beta-open-tos">
                                    <i class="bi bi-file-earmark-text mr-1"></i>Terms
                                </b-button>
                                <b-button variant="outline-success" size="sm" class="py-1 px-2 extra-small flex-fill mr-1" @click="openLegal('privacy')" data-test="btn-beta-open-privacy">
                                    <i class="bi bi-shield-lock mr-1"></i>Privacy
                                </b-button>
                                <b-button variant="outline-info" size="sm" class="py-1 px-2 extra-small flex-fill" @click="openLegal('security')" data-test="btn-beta-open-security">
                                    <i class="bi bi-shield-check mr-1"></i>Security
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- Footer Tip -->
                    <div class="mt-4 pt-3 border-top drawer-border text-center extra-small text-muted">
                        Toggle drawer anytime with <kbd>Ctrl</kbd> + <kbd>`</kbd> or <kbd>Alt</kbd> + <kbd>D</kbd>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import eventBus from '../event-bus';
import { getAppEnvironment } from '../envHelper';
import accountMixin from '../mixinAccount';

// Beta sidepanel release history cutoff: only show entries from October 10th, 2026 onwards
const CHANGELOG_CUTOFF_DATE = process.env.VUE_APP_BETA_CHANGELOG_START_DATE || '2026-10-10';

export default {
    name: 'BetaSystemInfo',
    mixins: [accountMixin],
    data() {
        let initialSection = 'changelog';
        try {
            const savedSec = localStorage.getItem('velocytrack_beta_info_section');
            if (savedSec === 'system' || savedSec === 'changelog') {
                initialSection = savedSec;
            }
        } catch (e) {
            // fallback
        }

        return {
            activeSection: initialSection,
            isOpen: false,
            searchQuery: '',
            changelogCategoryFilter: 'all',
            expandedEntries: {},
            changelogCutoffDate: CHANGELOG_CUTOFF_DATE,
            changelogList: [],
            loadingList: false,
            loadedEntries: {},
            loadingEntries: {}
        };
    },
    watch: {
        isOpen(val) {
            if (val && this.changelogList.length === 0) {
                this.fetchChangelogIndex();
            }
        }
    },
    computed: {
        triggerTooltipOptions() {
            return {
                title: 'Beta Info & Changelog (Ctrl + ` or Alt + D)'
            };
        },
        currentUser() {
            return this.$store.state.account || {};
        },
        currentUserName() {
            const acc = this.currentUser;
            return (acc.fullName || acc.FullName || acc.name || acc.displayName || '').toLowerCase().trim();
        },
        currentUserEmail() {
            const acc = this.currentUser;
            return (acc.email || acc.Email || '').toLowerCase().trim();
        },
        totalFeaturesEntries() {
            return this.changelogList.filter(e => (e.featuresCount > 0) || (Array.isArray(e.categories) && e.categories.includes('Feature'))).length;
        },
        totalFixesEntries() {
            return this.changelogList.filter(e => (e.fixesCount > 0) || (Array.isArray(e.categories) && e.categories.includes('Fix')) || /\bfix\b|\bfixes\b|\bhotfix\b/i.test(e.title || '')).length;
        },
        totalBetaReportEntries() {
            return this.changelogList.filter(e => e.hasBetaFixes || (e.reportedFixesCount > 0)).length;
        },
        latestBuild() {
            return this.changelogList.length > 0 ? this.changelogList[0] : null;
        },
        pinnedBuild() {
            if (this.changelogCategoryFilter === 'all') {
                return this.latestBuild;
            }
            return this.filteredChangelogs.length > 0 ? this.filteredChangelogs[0] : null;
        },
        pinnedBuildBadgeText() {
            if (this.changelogCategoryFilter === 'features') return 'LATEST FEATURES';
            if (this.changelogCategoryFilter === 'fixes') return 'LATEST FIXES';
            if (this.changelogCategoryFilter === 'beta-reports') return 'LATEST BETA REPORT';
            return 'CURRENT BUILD';
        },
        pinnedBuildBadgeIcon() {
            if (this.changelogCategoryFilter === 'features') return 'bi-stars';
            if (this.changelogCategoryFilter === 'fixes') return 'bi-bug-fill';
            if (this.changelogCategoryFilter === 'beta-reports') return 'bi-star-fill';
            return 'bi-check-circle-fill';
        },
        pinnedBuildBadgeClass() {
            if (this.changelogCategoryFilter === 'features') return 'badge-info';
            if (this.changelogCategoryFilter === 'fixes') return 'badge-success';
            if (this.changelogCategoryFilter === 'beta-reports') return 'badge-warning text-dark font-weight-bold';
            return 'badge-success';
        },
        filteredChangelogs() {
            let list = this.changelogList.filter(this.isEntryAllowed);
            if (this.changelogCategoryFilter === 'features') {
                list = list.filter(e => (e.featuresCount > 0) || (Array.isArray(e.categories) && e.categories.includes('Feature')));
            } else if (this.changelogCategoryFilter === 'fixes') {
                list = list.filter(e => (e.fixesCount > 0) || (Array.isArray(e.categories) && e.categories.includes('Fix')) || /\bfix\b|\bfixes\b|\bhotfix\b/i.test(e.title || ''));
            } else if (this.changelogCategoryFilter === 'beta-reports') {
                list = list.filter(e => e.hasBetaFixes || (e.reportedFixesCount > 0));
            }

            if (!this.searchQuery) {
                return list;
            }
            const q = this.searchQuery.trim().toLowerCase();
            return list.filter(c => {
                if (c.title && c.title.toLowerCase().includes(q)) return true;
                if (c.date && c.date.includes(q)) return true;
                if (Array.isArray(c.categories) && c.categories.some(cat => cat.toLowerCase().includes(q))) return true;
                return false;
            });
        },
        userEmail() {
            const acc = this.$store.state.account;
            return acc?.email || acc?.Email || 'beta@velocytrack.com';
        },
        userPlanLabel() {
            return 'Beta Plan';
        },
        appVersion() {
            return process.env.VUE_APP_VERSION || '0.1.0';
        },
        gitCommit() {
            return process.env.VUE_APP_GIT_COMMIT || 'local';
        },
        releaseChannel() {
            return process.env.VUE_APP_RELEASE_CHANNEL || 'Beta';
        },
        envNameNormalized() {
            return this.currentEnvironment || getAppEnvironment(this.$store.state.devSimulator?.envOverride);
        },
        envNameDisplay() {
            const env = this.envNameNormalized;
            if (env === 'production') return 'Production';
            if (env === 'beta') return 'Beta';
            return 'Development';
        },
        envBadgeLabel() {
            const raw = this.envNameNormalized;
            if (raw === 'production' || raw === 'prod') return 'PROD';
            if (raw === 'beta' || raw === 'staging') return 'BETA';
            if (raw === 'qa') return 'QA';
            return 'DEV';
        },
        formattedBuildTime() {
            try {
                const bt = process.env.BUILD_TIME;
                if (!bt) return 'N/A';
                const d = new Date(bt);
                return d.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
            } catch (e) {
                return 'N/A';
            }
        },
        triggerTooltip() {
            return 'Beta System Info & Changelog (Ctrl+` or Alt+D)';
        },
        isBetaWelcomeOpen() {
            return Boolean(this.$store.state.isBetaWelcomeOpen);
        },
        hasAcknowledgedBetaWelcome() {
            if (this.isActualDevHost || this.isDemo) return true;
            return Boolean(this.$store.state.hasAcknowledgedBetaWelcome);
        },
        isPanelAvailable() {
            return Boolean(this.$store.state.isAuthenticated) && !this.isBetaWelcomeOpen && this.hasAcknowledgedBetaWelcome;
        }
    },
    created() {
        this.loadSettings();
        eventBus.$on('open-power-panel', this.openPanel);
        eventBus.$on('toggle-power-panel', this.togglePanel);
        eventBus.$on('open-beta-info', this.openPanel);
        eventBus.$on('toggle-beta-info', this.togglePanel);
        eventBus.$on('open-dev-panel', this.openPanel);
        eventBus.$on('toggle-dev-panel', this.togglePanel);
        this.onBetaWelcomeShown = () => {
            this.isOpen = false;
        };
        eventBus.$on('beta-welcome-shown', this.onBetaWelcomeShown);
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeyDown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        eventBus.$off('open-power-panel', this.openPanel);
        eventBus.$off('toggle-power-panel', this.togglePanel);
        eventBus.$off('open-beta-info', this.openPanel);
        eventBus.$off('toggle-beta-info', this.togglePanel);
        eventBus.$off('open-dev-panel', this.openPanel);
        eventBus.$off('toggle-dev-panel', this.togglePanel);
        if (this.onBetaWelcomeShown) {
            eventBus.$off('beta-welcome-shown', this.onBetaWelcomeShown);
        }
    },
    methods: {
        loadSettings() {
            try {
                const savedSec = localStorage.getItem('velocytrack_beta_info_section');
                if (savedSec === 'system' || savedSec === 'changelog') {
                    this.activeSection = savedSec;
                }
            } catch (e) {
                // fallback
            }
        },
        selectSection(sec) {
            this.activeSection = sec;
            try {
                localStorage.setItem('velocytrack_beta_info_section', sec);
            } catch (e) {
                // fallback
            }
        },
        openPanel() {
            if (!this.isPanelAvailable) return;
            this.isOpen = true;
        },
        togglePanel() {
            if (!this.isPanelAvailable) return;
            this.isOpen = !this.isOpen;
        },
        handleKeyDown(e) {
            if ((e.ctrlKey && e.code === 'Backquote') || (e.altKey && e.code === 'KeyD')) {
                if (!this.isPanelAvailable) return;
                e.preventDefault();
                this.togglePanel();
            } else if (e.key === 'Escape' && this.isOpen) {
                this.isOpen = false;
            }
        },
        isExpanded(id) {
            return !!this.expandedEntries[id];
        },
        toggleEntry(id) {
            const nextState = !this.expandedEntries[id];
            this.$set(this.expandedEntries, id, nextState);
            if (nextState && !this.loadedEntries[id]) {
                this.loadEntryDetails(id);
            }
        },
        isEntryAllowed(entry) {
            if (!entry || !entry.date) return false;
            const dateStr = String(entry.date).trim().substring(0, 10);
            return dateStr >= CHANGELOG_CUTOFF_DATE;
        },
        async fetchChangelogIndex() {
            this.loadingList = true;
            try {
                const baseUrl = window.location.origin;
                const resp = await fetch(`${baseUrl}/changelogs/index.json`);
                if (resp.ok) {
                    const data = await resp.json();
                    const rawList = Array.isArray(data) ? data : [];
                    this.changelogList = rawList.filter(this.isEntryAllowed);
                    if (this.changelogList.length > 0) {
                        const topId = this.changelogList[0].id;
                        this.loadEntryDetails(topId);
                    }
                } else {
                    const bundled = await import('@/data/changelogs.json');
                    const items = bundled.default || bundled;
                    const rawList = (Array.isArray(items) ? items : []).map(entry => ({
                        id: entry.id,
                        date: entry.date,
                        title: entry.title,
                        categories: Array.from(new Set((entry.sections || []).map(s => s.category))),
                        itemCount: entry.totalItems || (entry.sections || []).reduce((acc, s) => acc + (s.items || []).length, 0)
                    }));
                    this.changelogList = rawList.filter(this.isEntryAllowed);
                    if (this.changelogList.length > 0) {
                        const topId = this.changelogList[0].id;
                        this.loadEntryDetails(topId);
                    }
                }
            } catch (e) {
                try {
                    const bundled = await import('@/data/changelogs.json');
                    const items = bundled.default || bundled;
                    const rawList = (Array.isArray(items) ? items : []).map(entry => ({
                        id: entry.id,
                        date: entry.date,
                        title: entry.title,
                        categories: Array.from(new Set((entry.sections || []).map(s => s.category))),
                        itemCount: entry.totalItems || (entry.sections || []).reduce((acc, s) => acc + (s.items || []).length, 0)
                    }));
                    this.changelogList = rawList.filter(this.isEntryAllowed);
                    if (this.changelogList.length > 0) {
                        const topId = this.changelogList[0].id;
                        this.loadEntryDetails(topId);
                    }
                } catch (err) {
                    // Ignore
                }
            } finally {
                this.loadingList = false;
            }
        },
        async loadEntryDetails(id) {
            if (this.loadedEntries[id] || this.loadingEntries[id]) return;
            this.$set(this.loadingEntries, id, true);
            try {
                const baseUrl = window.location.origin;
                const resp = await fetch(`${baseUrl}/changelogs/entries/${id}.json`);
                if (resp.ok) {
                    const data = await resp.json();
                    this.$set(this.loadedEntries, id, data);
                } else {
                    const bundled = await import('@/data/changelogs.json');
                    const items = bundled.default || bundled;
                    const found = (Array.isArray(items) ? items : []).find(e => e.id === id);
                    if (found) {
                        this.$set(this.loadedEntries, id, found);
                    }
                }
            } catch (e) {
                try {
                    const bundled = await import('@/data/changelogs.json');
                    const items = bundled.default || bundled;
                    const found = (Array.isArray(items) ? items : []).find(e => e.id === id);
                    if (found) {
                        this.$set(this.loadedEntries, id, found);
                    }
                } catch (err) {
                    // Ignore
                }
            } finally {
                this.$set(this.loadingEntries, id, false);
            }
        },
        getCategoryBadgeClass(category) {
            switch (category) {
                case 'Fix': return 'badge-warning text-dark font-weight-bold';
                case 'Feature': return 'badge-info font-weight-bold';
                case 'Integration': return 'badge-primary font-weight-bold';
                case 'Improvement': return 'badge-secondary font-weight-bold';
                default: return 'badge-dark border border-secondary text-light font-weight-bold';
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            try {
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const m = parseInt(parts[1], 10) - 1;
                    return `${months[m]} ${parseInt(parts[2], 10)}, ${parts[0]}`;
                }
            } catch (e) {
                // fallback
            }
            return dateStr;
        },
        getItemText(item) {
            const raw = typeof item === 'string' ? item : (item && item.text ? item.text : '');
            return raw.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
        },
        isItemReported(item) {
            return !!(item && item.isUserReported);
        },
        getItemReporter(item) {
            return item && item.reporter ? item.reporter : '';
        },
        getItemReportId(item) {
            return item && item.reportId ? item.reportId : '';
        },
        isReportedForCurrentUser(item) {
            if (!item || !item.isUserReported || !item.reporter) return false;
            const rep = (item.reporter || '').toLowerCase().trim();
            if (!rep) return false;
            const name = this.currentUserName;
            const email = this.currentUserEmail;
            if (name && (rep.includes(name) || name.includes(rep))) return true;
            if (email && (rep.includes(email) || email.includes(rep))) return true;
            return false;
        },
        setCategoryFilter(category) {
            this.changelogCategoryFilter = category;
            this.$nextTick(() => {
                if (this.pinnedBuild && !this.loadedEntries[this.pinnedBuild.id]) {
                    this.loadEntryDetails(this.pinnedBuild.id);
                }
            });
        },
        getEntryFilterCountLabel(entry) {
            if (!entry) return '';
            if (this.changelogCategoryFilter === 'fixes') {
                const count = entry.fixesCount || 1;
                return `${count} ${count === 1 ? 'fix' : 'fixes'}`;
            }
            if (this.changelogCategoryFilter === 'beta-reports') {
                const count = entry.reportedFixesCount || (entry.hasBetaFixes ? 1 : 0);
                return `${count} ${count === 1 ? 'beta report' : 'beta reports'}`;
            }
            if (this.changelogCategoryFilter === 'features') {
                const count = entry.featuresCount || entry.itemCount || 1;
                return `${count} ${count === 1 ? 'feature' : 'features'}`;
            }
            return `${entry.itemCount || 1} ${entry.itemCount === 1 ? 'change' : 'changes'}`;
        },
        isItemFix(item, sec) {
            if (!item) return false;
            if (item.isFix || item.category === 'Fix') return true;
            if (sec && sec.category === 'Fix') return true;
            const text = (typeof item === 'string' ? item : item.text || '').toLowerCase().trim();
            return /^(?:\*\*|\*|\[)?(?:fixed|fix|resolved|hotfix|corrected|defect|bug)\b/i.test(text);
        },
        getFilteredSections(entry) {
            if (!entry || !entry.sections) return [];
            if (this.changelogCategoryFilter === 'all') return entry.sections;

            const filtered = [];
            for (const sec of entry.sections) {
                const items = (sec.items || []).filter(it => {
                    const isFix = this.isItemFix(it, sec);
                    if (this.changelogCategoryFilter === 'fixes') {
                        return isFix;
                    }
                    if (this.changelogCategoryFilter === 'beta-reports') {
                        return !!(it && it.isUserReported);
                    }
                    if (this.changelogCategoryFilter === 'features') {
                        return !isFix;
                    }
                    return true;
                });

                if (items.length > 0) {
                    filtered.push({
                        ...sec,
                        items
                    });
                }
            }
            return filtered.length > 0 ? filtered : entry.sections;
        },
        getTopItems(entry, count) {
            if (!entry || !entry.sections) return [];
            const sections = this.getFilteredSections(entry);
            const result = [];
            for (const sec of sections) {
                if (Array.isArray(sec.items)) {
                    for (const it of sec.items) {
                        result.push(it);
                        if (result.length >= count) return result;
                    }
                }
            }
            return result;
        },
        openFeedbackModal() {
            this.isOpen = false;
            eventBus.$emit('open-feedback-modal', 'feedback');
        },
        openLegal(tab = 'terms') {
            this.isOpen = false;
            eventBus.$emit('open-legal', tab);
        }
    }
};
</script>

<style scoped>
/* CSS Tokens for Dual Theme Support */
.power-control-panel-wrapper,
.power-drawer-panel {
    position: relative;
    z-index: 1050;
    color-scheme: light;
    --drawer-bg: #ffffff;
    --drawer-header-bg: #f8fafc;
    --drawer-nav-bg: #f1f5f9;
    --drawer-nav-segment-bg: #e2e8f0;
    --drawer-nav-btn-color: #64748b;
    --drawer-nav-btn-hover-color: #0f172a;
    --drawer-nav-btn-hover-bg: rgba(0, 0, 0, 0.05);
    --drawer-text: #334155;
    --drawer-text-heading: #0f172a;
    --drawer-text-muted: #64748b;
    --drawer-border: #e2e8f0;
    --drawer-card-bg: #ffffff;
    --drawer-card-hover-bg: #f8fafc;
    --drawer-card-border: #e2e8f0;
    --drawer-card-border-hover: #cbd5e1;
    --drawer-input-bg: #ffffff;
    --drawer-input-color: #1e293b;
    --drawer-input-border: #cbd5e1;
    --drawer-input-prepend-bg: #f8fafc;
    --drawer-kbd-bg: #f1f5f9;
    --drawer-kbd-color: #334155;
    --drawer-kbd-border: #cbd5e1;
    --drawer-pill-bg: #f1f5f9;
    --drawer-pill-border: #cbd5e1;
    --drawer-pill-color: #475569;
    --drawer-pill-hover-bg: #e2e8f0;
    --drawer-pill-hover-color: #0f172a;
    --drawer-pill-count-bg: rgba(0, 0, 0, 0.08);
    --drawer-pill-count-color: #334155;
    --drawer-pinned-bg: linear-gradient(145deg, #f0f9ff, #e0f2fe);
    --drawer-pinned-border: rgba(56, 189, 248, 0.5);
    --drawer-pinned-color: #0f172a;
    --drawer-trigger-bg: #ffffff;
    --drawer-trigger-color: #334155;
    --drawer-trigger-border: #cbd5e1;
    --drawer-trigger-hover-bg: #f8fafc;
    --drawer-scrollbar-thumb: #cbd5e1;
    --drawer-scrollbar-thumb-hover: #94a3b8;
    --drawer-scrollbar-track: rgba(0, 0, 0, 0.04);
}

[data-theme="dark"] .power-control-panel-wrapper,
[data-theme="dark"] .power-drawer-panel,
[data-theme="dark"] .power-trigger-tab {
    color-scheme: dark;
    --drawer-bg: #0f172a;
    --drawer-header-bg: #1e293b;
    --drawer-nav-bg: #162032;
    --drawer-nav-segment-bg: #0f172a;
    --drawer-nav-btn-color: #94a3b8;
    --drawer-nav-btn-hover-color: #f8fafc;
    --drawer-nav-btn-hover-bg: rgba(255, 255, 255, 0.08);
    --drawer-text: #e2e8f0;
    --drawer-text-heading: #ffffff;
    --drawer-text-muted: #94a3b8;
    --drawer-border: #334155;
    --drawer-card-bg: #1e293b;
    --drawer-card-hover-bg: #233147;
    --drawer-card-border: #334155;
    --drawer-card-border-hover: #475569;
    --drawer-input-bg: #1e293b;
    --drawer-input-color: #f8fafc;
    --drawer-input-border: #334155;
    --drawer-input-prepend-bg: #1e293b;
    --drawer-kbd-bg: #334155;
    --drawer-kbd-color: #f8fafc;
    --drawer-kbd-border: #475569;
    --drawer-pill-bg: #1e293b;
    --drawer-pill-border: #475569;
    --drawer-pill-color: #e2e8f0;
    --drawer-pill-hover-bg: #334155;
    --drawer-pill-hover-color: #ffffff;
    --drawer-pill-count-bg: rgba(255, 255, 255, 0.14);
    --drawer-pill-count-color: #f1f5f9;
    --drawer-pinned-bg: linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.8));
    --drawer-pinned-border: rgba(56, 189, 248, 0.35);
    --drawer-pinned-color: #f8fafc;
    --drawer-trigger-bg: #1e293b;
    --drawer-trigger-color: #e2e8f0;
    --drawer-trigger-border: #334155;
    --drawer-trigger-hover-bg: #0f172a;
    --drawer-scrollbar-thumb: #3b4758;
    --drawer-scrollbar-thumb-hover: #4f5f76;
    --drawer-scrollbar-track: rgba(15, 23, 42, 0.6);
}

.drawer-heading-text {
    color: var(--drawer-text-heading) !important;
}

.drawer-body-text {
    color: var(--drawer-text) !important;
}

.drawer-border {
    border-color: var(--drawer-border) !important;
}

/* Changelog Category Filter Pills */
.changelog-category-pills {
    gap: 7px;
}

.cat-filter-btn {
    background: var(--drawer-pill-bg);
    border: 1px solid var(--drawer-pill-border);
    color: var(--drawer-pill-color);
    font-size: 0.75rem;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-flex;
    align-items: center;
    user-select: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    outline: none;
}

.cat-filter-btn:hover {
    color: var(--drawer-pill-hover-color);
    border-color: var(--drawer-card-border-hover);
    background: var(--drawer-pill-hover-bg);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.cat-filter-btn:active {
    transform: translateY(0);
}

.cat-filter-btn:focus-visible {
    box-shadow: 0 0 0 2px #38bdf8;
}

.cat-count-badge {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1;
    padding: 2px 6px;
    border-radius: 9999px;
    background: var(--drawer-pill-count-bg);
    color: var(--drawer-pill-count-color);
    transition: all 0.15s ease;
}

/* Category-specific unselected badge accents */
.cat-filter-btn.cat-filter-features:not(.active) .cat-count-badge {
    background: rgba(56, 189, 248, 0.2);
    color: #0284c7;
}

[data-theme="dark"] .cat-filter-btn.cat-filter-features:not(.active) .cat-count-badge {
    color: #7dd3fc;
}

.cat-filter-btn.cat-filter-fixes:not(.active) .cat-count-badge {
    background: rgba(16, 185, 129, 0.2);
    color: #059669;
}

[data-theme="dark"] .cat-filter-btn.cat-filter-fixes:not(.active) .cat-count-badge {
    color: #6ee7b7;
}

.cat-filter-btn.cat-filter-beta:not(.active) .cat-count-badge {
    background: rgba(245, 158, 11, 0.2);
    color: #b45309;
    border: 1px solid rgba(245, 158, 11, 0.35);
}

[data-theme="dark"] .cat-filter-btn.cat-filter-beta:not(.active) .cat-count-badge {
    color: #fde68a;
    border: 1px solid rgba(245, 158, 11, 0.45);
}

/* Active states with category-distinct accents */
.cat-filter-btn.active {
    background: #2563eb;
    border-color: #3b82f6;
    color: #ffffff;
    box-shadow: 0 2px 10px rgba(37, 99, 235, 0.4);
}
.cat-filter-btn.active .cat-count-badge {
    background: rgba(255, 255, 255, 0.3);
    color: #ffffff;
}

.cat-filter-btn.cat-filter-features.active {
    background: #0284c7;
    border-color: #38bdf8;
    box-shadow: 0 2px 10px rgba(2, 132, 199, 0.4);
}

.cat-filter-btn.cat-filter-fixes.active {
    background: #059669;
    border-color: #10b981;
    box-shadow: 0 2px 10px rgba(5, 150, 105, 0.4);
}

.cat-filter-btn.cat-filter-beta.active {
    background: #d97706;
    border-color: #f59e0b;
    box-shadow: 0 2px 10px rgba(217, 119, 6, 0.4);
}

.badge-beta-reported {
    background-color: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    color: #b45309;
    font-weight: 600;
}

[data-theme="dark"] .badge-beta-reported {
    background-color: rgba(245, 158, 11, 0.18);
    border-color: rgba(245, 158, 11, 0.45);
    color: #fbbf24;
}

/* Discreet handle on the left edge */
.power-trigger-tab {
    position: fixed;
    top: 70px;
    left: 0;
    z-index: 1045;
    background-color: var(--drawer-trigger-bg) !important;
    color: var(--drawer-trigger-color) !important;
    border: 1px solid var(--drawer-trigger-border) !important;
    border-left: none !important;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    padding: 6px 8px;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 0.85;
}

.power-trigger-tab:hover {
    opacity: 1;
    transform: translateX(2px);
    background-color: var(--drawer-trigger-hover-bg) !important;
    color: var(--drawer-text-heading) !important;
    border-color: var(--drawer-card-border-hover) !important;
}

.power-trigger-tab.is-open {
    opacity: 1;
    background-color: var(--drawer-trigger-hover-bg) !important;
}

.power-trigger-icon {
    font-size: 0.85rem;
    color: #f59e0b;
}

.power-trigger-label {
    letter-spacing: 0.5px;
}

.badge-beta-amber {
    background-color: #b45309 !important;
    border: 1px solid #d97706 !important;
    color: #ffffff !important;
    font-weight: 700;
}

.beta-channel-badge {
    background-color: #d97706 !important;
    color: #ffffff !important;
    font-weight: 800;
    font-size: 0.72rem;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    display: inline-block;
    line-height: 1.2;
}

[data-theme="dark"] .beta-channel-badge {
    color: #000000 !important;
}

/* Pinned Current Build Card */
.pinned-build-card {
    background: var(--drawer-pinned-bg) !important;
    border-color: var(--drawer-pinned-border) !important;
    box-shadow: 0 4px 12px rgba(56, 189, 248, 0.08);
    color: var(--drawer-pinned-color) !important;
}

/* Changelog Entry Card */
.changelog-entry-card {
    background-color: var(--drawer-card-bg) !important;
    border-color: var(--drawer-card-border) !important;
    color: var(--drawer-text) !important;
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.changelog-entry-card:hover {
    background-color: var(--drawer-card-hover-bg) !important;
    border-color: var(--drawer-card-border-hover) !important;
}

.changelog-entry-card.entry-expanded {
    border-color: rgba(56, 189, 248, 0.4) !important;
}

.entry-title {
    line-height: 1.35;
}

.cursor-pointer {
    cursor: pointer;
}

/* System Env Dot */
.system-env-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    display: inline-block;
    background-color: #0dcaf0;
}

.system-env-dot.dot-development {
    background-color: #0dcaf0;
    box-shadow: 0 0 6px #0dcaf0;
}

.system-env-dot.dot-qa,
.system-env-dot.dot-staging {
    background-color: #ffc107;
    box-shadow: 0 0 6px #ffc107;
}

.system-env-dot.dot-production {
    background-color: #28a745;
    box-shadow: 0 0 6px #28a745;
}

/* Backdrop */
.power-drawer-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    backdrop-filter: blur(2px);
}

/* Drawer panel */
.power-drawer-panel {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 440px;
    max-width: 94vw;
    background-color: var(--drawer-bg) !important;
    color: var(--drawer-text) !important;
    border-right: 1px solid var(--drawer-border) !important;
    z-index: 1060;
    display: flex;
    flex-direction: column;
}

.power-drawer-header {
    background-color: var(--drawer-header-bg) !important;
    border-bottom: 1px solid var(--drawer-border) !important;
    flex-shrink: 0;
}

/* Inside Section Menu */
.power-drawer-nav {
    background-color: var(--drawer-nav-bg) !important;
    border-bottom: 1px solid var(--drawer-border) !important;
    flex-shrink: 0;
}

.nav-segment-wrapper {
    background-color: var(--drawer-nav-segment-bg) !important;
    border: 1px solid var(--drawer-border) !important;
    gap: 4px;
}

.nav-segment-btn {
    background: transparent !important;
    border: none !important;
    color: var(--drawer-nav-btn-color) !important;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.nav-segment-btn:hover {
    color: var(--drawer-nav-btn-hover-color) !important;
    background-color: var(--drawer-nav-btn-hover-bg) !important;
}

.nav-segment-btn.active {
    background-color: #2563eb !important;
    color: #ffffff !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25) !important;
}

.power-drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--drawer-scrollbar-thumb) var(--drawer-scrollbar-track);
}

.power-drawer-body::-webkit-scrollbar {
    width: 6px;
}

.power-drawer-body::-webkit-scrollbar-track {
    background: var(--drawer-scrollbar-track);
}

.power-drawer-body::-webkit-scrollbar-thumb {
    background-color: var(--drawer-scrollbar-thumb);
    border-radius: 3px;
}

.power-drawer-body::-webkit-scrollbar-thumb:hover {
    background-color: var(--drawer-scrollbar-thumb-hover);
}

.power-card {
    background-color: var(--drawer-card-bg) !important;
    border-color: var(--drawer-card-border) !important;
    color: var(--drawer-text) !important;
}

.extra-small {
    font-size: 0.75rem;
}

kbd {
    background-color: var(--drawer-kbd-bg) !important;
    color: var(--drawer-kbd-color) !important;
    padding: 2px 5px;
    border-radius: 4px;
    font-size: 0.7rem;
    border: 1px solid var(--drawer-kbd-border) !important;
}

/* Child Elements Theme Rules */
.power-drawer-panel ::v-deep .text-muted {
    color: var(--drawer-text-muted) !important;
}

.power-drawer-panel ::v-deep .border {
    border-color: var(--drawer-border) !important;
}

.power-drawer-panel ::v-deep .border-secondary {
    border-color: var(--drawer-border) !important;
}

.power-drawer-panel ::v-deep .border-top {
    border-top-color: var(--drawer-border) !important;
}

.power-drawer-panel ::v-deep .border-bottom {
    border-bottom-color: var(--drawer-border) !important;
}

/* Form inputs & search group inside drawer */
.drawer-search-prepend,
.power-drawer-panel ::v-deep .input-group-prepend .input-group-text,
.power-drawer-panel ::v-deep .input-group-text {
    background-color: var(--drawer-input-prepend-bg) !important;
    border-color: var(--drawer-input-border) !important;
    color: var(--drawer-text-muted) !important;
}

.drawer-search-input,
.power-drawer-panel ::v-deep .form-control {
    background-color: var(--drawer-input-bg) !important;
    border-color: var(--drawer-input-border) !important;
    color: var(--drawer-input-color) !important;
}

.power-drawer-panel ::v-deep .form-control:focus {
    background-color: var(--drawer-input-bg) !important;
    border-color: #38bdf8 !important;
    color: var(--drawer-input-color) !important;
    box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.25) !important;
}

.power-drawer-panel ::v-deep .form-control::placeholder {
    color: var(--drawer-text-muted) !important;
}

/* Action buttons */
.drawer-clear-btn,
.power-drawer-panel ::v-deep .btn-outline-secondary {
    color: var(--drawer-text-muted) !important;
    border-color: var(--drawer-input-border) !important;
    background-color: transparent !important;
}

.power-drawer-panel ::v-deep .btn-outline-secondary:hover {
    color: var(--drawer-text-heading) !important;
    background-color: var(--drawer-card-hover-bg) !important;
    border-color: var(--drawer-card-border-hover) !important;
}

.power-drawer-panel ::v-deep .btn-outline-info {
    color: #0284c7 !important;
    border-color: #0284c7 !important;
    background-color: transparent !important;
}

[data-theme="dark"] .power-drawer-panel ::v-deep .btn-outline-info {
    color: #38bdf8 !important;
    border-color: #0284c7 !important;
}

.power-drawer-panel ::v-deep .btn-outline-info:hover {
    background-color: #0284c7 !important;
    color: #ffffff !important;
    border-color: #0284c7 !important;
}

.power-drawer-panel ::v-deep .btn-link {
    color: var(--drawer-text-muted) !important;
    text-decoration: none !important;
}

.power-drawer-panel ::v-deep .btn-link:hover,
.power-drawer-panel ::v-deep .btn-link:focus {
    color: var(--drawer-text-heading) !important;
    text-decoration: none !important;
}

/* Badges */
.power-drawer-panel ::v-deep .badge-secondary {
    background-color: var(--drawer-nav-segment-bg) !important;
    color: var(--drawer-text) !important;
    border: 1px solid var(--drawer-border) !important;
}

.power-drawer-panel ::v-deep .badge-dark {
    background-color: var(--drawer-card-bg) !important;
    color: var(--drawer-text) !important;
    border: 1px solid var(--drawer-border) !important;
}

.power-drawer-panel ::v-deep .badge-info {
    background-color: rgba(14, 165, 233, 0.18) !important;
    border: 1px solid rgba(14, 165, 233, 0.4) !important;
    color: #0369a1 !important;
}

[data-theme="dark"] .power-drawer-panel ::v-deep .badge-info {
    background-color: rgba(14, 165, 233, 0.22) !important;
    border-color: rgba(14, 165, 233, 0.45) !important;
    color: #7dd3fc !important;
}

.power-drawer-panel ::v-deep .badge-primary {
    background-color: rgba(59, 130, 246, 0.18) !important;
    border: 1px solid rgba(59, 130, 246, 0.4) !important;
    color: #1d4ed8 !important;
}

[data-theme="dark"] .power-drawer-panel ::v-deep .badge-primary {
    background-color: rgba(59, 130, 246, 0.22) !important;
    border-color: rgba(59, 130, 246, 0.45) !important;
    color: #93c5fd !important;
}

.power-drawer-panel ::v-deep .badge-success {
    background-color: rgba(34, 197, 94, 0.18) !important;
    border: 1px solid rgba(34, 197, 94, 0.4) !important;
    color: #15803d !important;
}

[data-theme="dark"] .power-drawer-panel ::v-deep .badge-success {
    background-color: rgba(34, 197, 94, 0.22) !important;
    border-color: rgba(34, 197, 94, 0.45) !important;
    color: #86efac !important;
}

.power-drawer-panel ::v-deep .badge-warning {
    background-color: rgba(245, 158, 11, 0.18) !important;
    border: 1px solid rgba(245, 158, 11, 0.4) !important;
    color: #b45309 !important;
}

[data-theme="dark"] .power-drawer-panel ::v-deep .badge-warning {
    background-color: rgba(245, 158, 11, 0.22) !important;
    border-color: rgba(245, 158, 11, 0.45) !important;
    color: #fde68a !important;
}

/* Spinner labels */
.power-drawer-panel ::v-deep .vt-loading-text {
    color: var(--drawer-text) !important;
}

.power-drawer-panel ::v-deep .vt-loading-subtext {
    color: var(--drawer-text-muted) !important;
}

/* Slide Transition */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-drawer-enter,
.slide-drawer-leave-to {
    transform: translateX(-100%);
}

/* Responsive Full-Screen on Mobile Phone (<576px) */
@media (max-width: 575.98px) {
    .power-trigger-tab {
        display: none !important;
    }

    .power-drawer-panel {
        width: 100% !important;
        width: 100vw !important;
        max-width: 100% !important;
        max-width: 100vw !important;
        height: 100% !important;
        height: 100vh !important;
        height: 100dvh !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        border-radius: 0 !important;
        border: none !important;
        z-index: 1060 !important;
    }

    .power-drawer-backdrop {
        z-index: 1055 !important;
    }

    .power-drawer-header {
        padding: 0.75rem 1rem !important;
    }

    .power-drawer-nav {
        padding: 0.5rem 0.75rem !important;
    }

    .nav-segment-btn {
        font-size: 0.72rem !important;
        padding: 6px 4px !important;
    }

    .power-drawer-body {
        padding: 0.85rem !important;
    }
}
</style>
